import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    roofType: '',
    projectType: '',
    timeline: '',
    message: '',
    agreement: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreement) {
      alert('Please acknowledge the $500 fee structure to continue.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('roofType', formData.roofType);
      formBody.append('projectType', formData.projectType);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);
      formBody.append('agreement', formData.agreement ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d6mf8kc6fojmejgajdb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          roofType: '',
          projectType: '',
          timeline: '',
          message: '',
          agreement: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-emerald-50/40 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Left Column - Information */}
          <div className="md:col-span-2 space-y-8">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
              <p className="text-base text-gray-700 leading-relaxed">
                Fill out the intake form and we'll reach out within 24 hours to discuss your project. We'll schedule a brief call to clarify details, then we'll start gathering competitive bids from vetted contractors in Wake County and the Triangle area.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <a href="tel:+19193497701" className="flex items-center gap-3 text-emerald-700 hover:text-emerald-800 transition-colors cursor-pointer">
                <i className="ri-phone-line text-2xl"></i>
                <span className="text-xl font-semibold">(919) 349-7701</span>
              </a>
              <a href="mailto:overunderroofing@gmail.com" className="flex items-center gap-3 text-emerald-700 hover:text-emerald-800 transition-colors cursor-pointer">
                <i className="ri-mail-line text-2xl"></i>
                <span className="text-base">overunderroofing@gmail.com</span>
              </a>
              <div className="flex items-center gap-3 text-gray-700">
                <i className="ri-map-pin-line text-2xl"></i>
                <span className="text-base">Serving Wake County &amp; Triangle Area</span>
              </div>
            </div>

            {/* Social Proof Badges */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 opacity-60">
                <i className="ri-shield-star-line text-3xl text-gray-600"></i>
                <span className="text-xs font-medium text-gray-600">BBB Accredited</span>
              </div>
              <div className="flex items-center gap-2 opacity-60">
                <i className="ri-google-line text-3xl text-gray-600"></i>
                <span className="text-xs font-medium text-gray-600">4.9★ Reviews</span>
              </div>
              <div className="flex items-center gap-2 opacity-60">
                <i className="ri-verified-badge-line text-3xl text-gray-600"></i>
                <span className="text-xs font-medium text-gray-600">Licensed</span>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="md:col-span-3">
            <form
              id="contact-intake-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                  placeholder="(919) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">Property Address *</label>
                <div className="relative">
                  <i className="ri-map-pin-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                    placeholder="123 Main St, Apex, NC 27502"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="roofType" className="block text-sm font-semibold text-gray-700 mb-2">Roof Type *</label>
                <select
                  id="roofType"
                  name="roofType"
                  value={formData.roofType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm appearance-none bg-white cursor-pointer"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
                >
                  <option value="">Select roof type</option>
                  <option value="asphalt-shingle">Asphalt Shingle</option>
                  <option value="metal">Metal</option>
                  <option value="tile">Tile</option>
                  <option value="slate">Slate</option>
                  <option value="flat">Flat</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">Project Type *</label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm appearance-none bg-white cursor-pointer"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
                >
                  <option value="">Select project type</option>
                  <option value="replacement">Replacement</option>
                  <option value="repair">Repair</option>
                  <option value="new-roof">New Roof</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Project Timeline *</label>
                <div className="flex flex-wrap gap-3">
                  {['ASAP', '1-3 months', '3-6 months', 'Planning'].map((option) => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="timeline"
                        value={option}
                        checked={formData.timeline === option}
                        onChange={handleChange}
                        required
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Additional Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm resize-none"
                  placeholder="Tell us about any specific concerns, damage, or requirements..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  checked={formData.agreement}
                  onChange={handleChange}
                  required
                  className="mt-1 cursor-pointer"
                />
                <label htmlFor="agreement" className="text-sm text-gray-700 cursor-pointer">
                  I understand the{' '}
                  <button
                    type="button"
                    onClick={() => {
                      const element = document.getElementById('pricing');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-emerald-700 underline hover:text-emerald-800 cursor-pointer"
                  >
                    $500 fee structure
                  </button>
                  {' '}and success-based pricing model
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white py-4 rounded-2xl font-bold text-base transition-all shadow-lg hover:shadow-xl hover:scale-105 disabled:scale-100 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Intake Form'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> Your intake form has been submitted. We'll reach out within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error:</strong> Something went wrong. Please try again or call us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
